// Streaming run state per thread (architecture §8.4, WP0). Fed by sse.ts from `run` events; the chat UI (WP4) renders the
// streaming bubble from it until the persisted message arrives through `message.created`.
import { create } from 'zustand';
import type { RunEvent } from '@hq/shared';

export interface ToolState {
  toolUseId: string;
  name: string;
  kind: 'client' | 'server' | 'mcp';
  status: 'laeuft' | 'ok' | 'fehler' | 'freigabe';
  summary: string | null;
  approvalId?: string;
}

export type RunUiStatus = 'laeuft' | 'done' | 'waiting_approval' | 'refused' | 'needs_continue' | 'error' | 'aborted';

export interface ThreadRun {
  runId: string;
  agentId: string;
  /** Streamed visible text of the current assistant message (all text blocks joined). */
  text: string;
  /** display:"updates" progress lines (thinking.update) */
  progress: string[];
  tools: Map<string, ToolState>;
  status: RunUiStatus;
  model: string | null;
  error: { code: string; messageDe: string; retryable: boolean } | null;
  /** Set by message.completed: the bubble disappears once this message id is in the messages cache. */
  completedMessageId: string | null;
}

export interface RunStore {
  runs: Record<string, ThreadRun>;
  apply(env: { runId: string; threadId: string; agentId: string }, e: RunEvent): void;
  /** Called when a persisted message arrived (message.created) or was found in the cache. */
  messagePersisted(threadId: string, messageId: string): void;
  clear(threadId: string): void;
}

const fresh = (runId: string, agentId: string): ThreadRun => ({
  runId, agentId, text: '', progress: [], tools: new Map(), status: 'laeuft', model: null, error: null, completedMessageId: null,
});

/** Pure reducer (unit-tested). Returns the next ThreadRun or null to drop it. */
export function reduceRun(prev: ThreadRun | undefined, env: { runId: string; agentId: string }, e: RunEvent): ThreadRun | null {
  let r: ThreadRun = prev && prev.runId === env.runId ? { ...prev, tools: new Map(prev.tools) } : fresh(env.runId, env.agentId);
  switch (e.type) {
    case 'run.started':
      r = { ...fresh(env.runId, env.agentId), model: e.model };
      break;
    case 'text.delta':
      r.text += e.text;
      r.completedMessageId = null;
      break;
    case 'thinking.update':
      if (e.text.trim()) r.progress = [...r.progress.slice(-19), e.text.trim()];
      break;
    case 'tool.started':
      r.tools.set(e.toolUseId, { toolUseId: e.toolUseId, name: e.name, kind: 'client', status: 'laeuft', summary: null });
      break;
    case 'tool.input_progress':
      break;
    case 'tool.finished': {
      const t = r.tools.get(e.toolUseId);
      r.tools.set(e.toolUseId, { toolUseId: e.toolUseId, name: e.name, kind: t?.kind ?? 'client', status: e.ok ? 'ok' : 'fehler', summary: e.summary, ...(t?.approvalId ? { approvalId: t.approvalId } : {}) });
      break;
    }
    case 'server_tool.started':
      r.tools.set(e.toolUseId, { toolUseId: e.toolUseId, name: e.name, kind: 'server', status: 'laeuft', summary: null });
      break;
    case 'server_tool.finished': {
      const t = r.tools.get(e.toolUseId);
      if (t) r.tools.set(e.toolUseId, { ...t, status: e.ok ? 'ok' : 'fehler' });
      break;
    }
    case 'mcp_tool.started':
      r.tools.set(e.toolUseId, { toolUseId: e.toolUseId, name: `${e.server}: ${e.name}`, kind: 'mcp', status: 'laeuft', summary: null });
      break;
    case 'mcp_tool.finished': {
      const t = r.tools.get(e.toolUseId);
      if (t) r.tools.set(e.toolUseId, { ...t, status: e.ok ? 'ok' : 'fehler' });
      break;
    }
    case 'approval.requested': {
      const t = r.tools.get(e.toolUseId);
      r.tools.set(e.toolUseId, { toolUseId: e.toolUseId, name: t?.name ?? e.title, kind: 'client', status: 'freigabe', summary: e.title, approvalId: e.approvalId });
      break;
    }
    case 'fallback':
      r.model = e.to;
      break;
    case 'message.completed':
      r.completedMessageId = e.messageId;
      break;
    case 'message.discarded':
      r.text = '';
      r.progress = [];
      r.completedMessageId = null;
      break;
    case 'run.usage':
      break;
    case 'run.error':
      r.error = { code: e.code, messageDe: e.messageDe, retryable: e.retryable };
      break;
    case 'run.finished':
      r.status = e.status;
      if (e.status === 'done' && !r.text && r.tools.size === 0) return null;
      break;
  }
  return r;
}

export const useRunStore = create<RunStore>((set, get) => ({
  runs: {},
  apply(env, e) {
    const next = reduceRun(get().runs[env.threadId], env, e);
    const runs = { ...get().runs };
    if (next) runs[env.threadId] = next;
    else delete runs[env.threadId];
    set({ runs });
  },
  messagePersisted(threadId, messageId) {
    const r = get().runs[threadId];
    if (!r || r.completedMessageId !== messageId) return;
    // The persisted message replaces the streaming text; tool chips stay until the run finishes.
    set({ runs: { ...get().runs, [threadId]: { ...r, text: '', progress: [], completedMessageId: null } } });
  },
  clear(threadId) {
    const runs = { ...get().runs };
    delete runs[threadId];
    set({ runs });
  },
}));

/** True while a run is active in the thread (composer shows "Stopp"). */
export const isRunActive = (r: ThreadRun | undefined) => r !== undefined && r.status === 'laeuft';
